<template>
  <!-- Everything the classic dashboard (/analytics/sales) shows, with the same
       links and modals, fed by the same /api/highlights + /api/inventory-analysis. -->
  <div>
    <div v-if="loading" class="row" style="row-gap:20px;">
      <div v-for="n in 3" :key="n" class="col-12 col-md-6 col-xl-4">
        <div class="bg-white border" style="border-radius:14px;border-color:#e9ecef !important;height:220px;"></div>
      </div>
    </div>

    <div v-else class="row" style="row-gap:20px;">

      <!-- Sales -->
      <div class="col-12 col-md-6 col-xl-4">
        <div class="bg-white border h-100 breakdown-card">
          <div class="breakdown-title"><i class="bi bi-graph-up"></i> Sales</div>
          <div class="breakdown-hero">{{ money(totalSales) }}</div>
          <template v-if="salesSources > 1">
            <div v-if="h.sales > 0" class="breakdown-row"><span>POS sales</span><span>{{ money(h.sales) }}</span></div>
            <a v-if="h.invoice_sales > 0" href="/invoices" class="breakdown-row breakdown-link"><span>Invoices ({{ h.invoice_count }})</span><span>{{ money(h.invoice_sales) }}</span></a>
            <a v-if="h.membership_sales > 0" href="/subscriptions" class="breakdown-row breakdown-link"><span>Memberships ({{ h.membership_count }})</span><span>{{ money(h.membership_sales) }}</span></a>
            <a v-if="h.reservation_sales > 0" href="/reservations" class="breakdown-row breakdown-link"><span>Reservations ({{ h.reservation_count }})</span><span>{{ money(h.reservation_sales) }}</span></a>
          </template>
          <div v-if="h.paid > 0" class="breakdown-row"><span>Paid</span><span>{{ money(h.paid) }}</span></div>
          <a v-if="h.balances > 0" :href="ordersUrl('has-balance=1&')" class="breakdown-row breakdown-link text-danger"><span>Balances</span><span>{{ money(h.balances) }}</span></a>
          <a v-if="h.discounts > 0" :href="ordersUrl('has-discount=1&')" class="breakdown-row breakdown-link text-danger"><span>Discounts</span><span>{{ money(h.discounts) }}</span></a>
          <a v-if="h.pending > 0" :href="ordersUrl('un-attended=1&')" class="breakdown-row breakdown-link text-danger"><span>Pending ({{ h.pending }})</span><span>{{ money(h.pendingValue) }}</span></a>
        </div>
      </div>

      <!-- Profit & loss -->
      <div class="col-12 col-md-6 col-xl-4">
        <div class="bg-white border h-100 breakdown-card">
          <div class="breakdown-title"><i class="bi bi-cash-coin"></i> Profits and Losses</div>
          <div class="breakdown-hero" :class="pnl < 0 ? 'text-danger' : ''">
            P&amp;L {{ pnl < 0 ? '(' + money(pnl) + ')' : money(pnl) }}
          </div>
          <div class="breakdown-row"><span>Gross profit</span><span>{{ money(h.profits) }}</span></div>
          <template v-if="h.invoice_profit || h.invoice_lines_without_cost">
            <div class="breakdown-row text-secondary small"><span>POS · Invoices</span><span>{{ money(h.pos_profits) }} · {{ money(h.invoice_profit) }}</span></div>
            <div v-if="h.invoice_lines_without_cost > 0" class="text-secondary small mt-1">
              {{ h.invoice_lines_without_cost }} invoiced line(s) have no buying price, so their profit isn't counted.
            </div>
          </template>
          <div v-if="Object.keys(h.expenseandPaymentsDifference || {}).length" class="text-secondary mt-2 mb-1 small">Sales less expenses and supplier payments, by payment method</div>
          <div v-for="(amount, method) in (h.expenseandPaymentsDifference || {})" :key="'d' + method" class="breakdown-row">
            <span>{{ method }}</span><span>{{ money(amount) }}</span>
          </div>
        </div>
      </div>

      <!-- Payment methods -->
      <div class="col-12 col-md-6 col-xl-4">
        <div class="bg-white border h-100 breakdown-card">
          <div class="breakdown-title"><i class="bi bi-credit-card"></i> Payment Methods</div>
          <div class="breakdown-hero">Received {{ money(h.totalAmountReceived) }}</div>
          <div v-for="(amount, method) in payments" :key="'p' + method" class="breakdown-row">
            <span>{{ method }}</span><span>{{ money(amount) }}</span>
          </div>
          <template v-if="h.enable_opening_cash && h.opening_cash_float > 0">
            <div class="breakdown-row"><span>Opening float</span><span>{{ money(h.opening_cash_float) }}</span></div>
            <div v-if="cashPaid > 0" class="breakdown-row font-weight-bold"><span>Expected in drawer</span><span>{{ money(h.opening_cash_float + cashPaid) }}</span></div>
          </template>
          <a v-if="transactions.length" href="#" class="d-inline-block mt-2 small font-weight-bold" @click.prevent="$refs.transactionsModal.show()">
            View all transactions <i class="bi bi-chevron-right"></i>
          </a>
        </div>
      </div>

      <!-- Expenses & wastes -->
      <div class="col-12 col-md-6 col-xl-4">
        <div class="bg-white border h-100 breakdown-card">
          <div class="breakdown-title"><i class="bi bi-wallet2"></i> Expenses &amp; Wastes</div>
          <div class="breakdown-hero text-danger">{{ money(h.total_expenses || h.expenses) }}</div>
          <a v-if="h.expenses > 0" :href="`/expenses?start-date=${startDate}&end-date=${endDate}`" class="breakdown-row breakdown-link text-danger"><span>From business cash</span><span>{{ money(h.expenses) }}</span></a>
          <div v-if="h.non_deductible_expenses > 0" class="breakdown-row text-secondary">
            <span>Monthly / external <small class="d-block">Not deducted from sales</small></span><span>{{ money(h.non_deductible_expenses) }}</span>
          </div>
          <div v-if="Object.keys(h.expensePayments || {}).length" class="text-secondary mt-2 mb-1 small">By payment method</div>
          <div v-for="(amount, method) in (h.expensePayments || {})" :key="'e' + method" class="breakdown-row text-danger">
            <span>{{ method || 'Unspecified' }}</span><span>{{ money(amount) }}</span>
          </div>
          <a v-if="h.wastes > 0" :href="`/wastes?start-date=${startDate}&end-date=${endDate}`" class="breakdown-row breakdown-link text-danger"><span>Wastes</span><span>{{ money(h.wastes) }}</span></a>
          <template v-if="h.supplier_payments > 0 || h.supplier_payments_external > 0">
            <a href="/purchases" class="breakdown-row breakdown-link">
              <span>Paid to suppliers <small class="d-block text-secondary">Stock bought — not an expense</small></span><span>{{ money(h.supplier_payments) }}</span>
            </a>
            <div v-for="(amount, method) in (h.supplier_payments_by_method || {})" :key="'s' + method" class="breakdown-row text-secondary small">
              <span>{{ method }}</span><span>{{ money(amount) }}</span>
            </div>
            <div v-if="h.supplier_payments_external > 0" class="breakdown-row text-secondary small">
              <span>From external sources</span><span>{{ money(h.supplier_payments_external) }}</span>
            </div>
          </template>
          <p v-if="!(h.total_expenses > 0) && !(h.wastes > 0)" class="text-secondary small mb-0">No expenses recorded for this period</p>
        </div>
      </div>

      <!-- Orders, invoices, quotes, deliveries (counts for the period) -->
      <div class="col-12 col-md-6 col-xl-4">
        <div class="bg-white border h-100 breakdown-card">
          <div class="breakdown-title"><i class="bi bi-cart-check"></i> Orders</div>
          <div class="breakdown-hero">{{ (h.orders || 0) + (h.invoice_count || 0) }}</div>
          <a v-if="!isInvoiceOnly" :href="ordersUrl('')" class="breakdown-row breakdown-link"><span>Orders</span><span>{{ h.orders || 0 }}</span></a>
          <a href="/invoices" class="breakdown-row breakdown-link"><span>Invoices</span><span>{{ h.invoice_count || 0 }}</span></a>
          <a href="/quotes" class="breakdown-row breakdown-link"><span>Quotes</span><span>{{ h.quote_count || 0 }}</span></a>
          <a href="/deliveries" class="breakdown-row breakdown-link"><span>Deliveries dispatched</span><span>{{ h.deliveries_dispatched || 0 }}</span></a>
          <a href="/deliveries" class="breakdown-row breakdown-link"><span>Deliveries delivered</span><span>{{ h.deliveries_delivered || 0 }}</span></a>
        </div>
      </div>

      <!-- Items sold / inventory analysis -->
      <div v-if="!isInvoiceOnly" class="col-12 col-md-6 col-xl-4">
        <div class="bg-white border h-100 breakdown-card">
          <div class="breakdown-title"><i class="bi bi-box-seam"></i> Unique Items Sold</div>
          <div class="breakdown-hero">{{ itemsSold.length }}</div>
          <a v-if="itemsSold.length" href="#" class="breakdown-row breakdown-link" @click.prevent="$refs.itemsModal.show()">
            <span>All items and quantities sold</span><i class="bi bi-chevron-right"></i>
          </a>
          <a href="#" class="breakdown-row breakdown-link" @click.prevent="$refs.inventoryModal.show()">
            <span>
              Inventory analysis
              <small class="d-block text-secondary">Opening stock, sold stock, remaining stock etc</small>
            </span>
            <span v-if="!analysisLoaded" class="spinner-border spinner-border-sm text-secondary"></span>
            <i v-else class="bi bi-chevron-right"></i>
          </a>
        </div>
      </div>

    </div>

    <!-- All transactions (classic "View All Transactions") -->
    <b-modal ref="transactionsModal" size="lg" hide-footer :title="`Transactions — ${money(h.totalAmountReceived)}`">
      <div class="mb-2">
        <span v-for="(amount, method) in payments" :key="'tm' + method" class="badge badge-secondary mr-1 mb-1">{{ method }}: {{ money(amount) }}</span>
      </div>
      <div class="table-responsive">
        <table class="table table-striped">
          <thead><tr><th>#</th><th>For</th><th>Amount</th><th>Mode</th><th>Order date</th><th>Payment date</th></tr></thead>
          <tbody>
            <tr v-for="(trx, i) in transactions" :key="i">
              <td>{{ i + 1 }}</td>
              <td>
                <a v-if="trx.invoice_id" :href="'/invoices/' + trx.invoice_id">{{ trx.order }}</a>
                <a v-else-if="trx.link" :href="trx.link">{{ trx.order }}</a>
                <span v-else-if="'link' in trx">{{ trx.order }}</span>
                <a v-else :href="'/orders?order-number=' + trx.order + '&source=/analytics/beta'">{{ trx.order }}</a>
              </td>
              <td>{{ money(trx.amount) }}</td>
              <td>{{ trx.paymentMethod }}</td>
              <td>{{ trx.order_date }}</td>
              <td>{{ trx.trx_date }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>

    <!-- All items and quantities sold -->
    <b-modal ref="itemsModal" size="lg" hide-footer :title="`All items and quantities sold · ${startDate} – ${endDate}`">
      <table class="table table-striped">
        <thead><tr><th>Item</th><th class="text-right">Qty sold</th></tr></thead>
        <tbody>
          <tr v-for="item in itemsSold" :key="item.id || item.title">
            <td><a class="text-dark" :href="itemLink(item.id)">{{ item.title }}</a></td>
            <td class="text-right"><a class="text-dark" :href="itemLink(item.id)">{{ item.quantity }}</a></td>
          </tr>
        </tbody>
      </table>
    </b-modal>

    <!-- Inventory and stock analysis -->
    <b-modal ref="inventoryModal" size="xl" hide-footer title="Inventory and Stock Analysis">
      <div class="text-right mb-2">
        <button class="btn btn-outline-secondary" :disabled="downloading" @click="downloadInventoryAnalysis">
          Download Stock Analysis <i class="bi bi-download"></i>
        </button>
      </div>
      <div v-if="!analysisLoaded" class="text-center py-4"><div class="spinner-border text-primary"></div></div>
      <div v-else class="table-responsive">
        <table class="table table-striped">
          <thead><tr><th>#</th><th>Item</th><th>Opening stock</th><th>Remaining</th><th>Restock count</th><th>Sold qty</th></tr></thead>
          <tbody>
            <tr v-for="(row, i) in inventoryAnalysis" :key="i">
              <td>{{ i + 1 }}</td>
              <td><a v-if="row.product" class="text-dark" :href="itemLink(row.product.id)">{{ row.product.product }}</a></td>
              <td>{{ row.opening_stock_balance }}</td>
              <td>{{ row.product ? row.product.remaining_units : '' }}</td>
              <td>{{ row.restock_count }}</td>
              <td>{{ row.sold_qty }}</td>
            </tr>
            <tr v-if="!inventoryAnalysis.length"><td colspan="6" class="text-center text-secondary">No stock movement in this period</td></tr>
          </tbody>
        </table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'ClassicBreakdown',

  props: {
    startDate: { type: String, required: true },
    endDate: { type: String, required: true },
    dayOfWeek: { type: String, default: null },
    isInvoiceOnly: { type: Boolean, default: false }
  },

  data() {
    return {
      loading: true,
      h: {},
      analysisLoaded: false,
      inventoryAnalysis: [],
      downloading: false
    };
  },

  computed: {
    params() {
      return { start_date: this.startDate, end_date: this.endDate, day_of_week: this.dayOfWeek || undefined };
    },
    totalSales() {
      return ['sales', 'invoice_sales', 'membership_sales', 'reservation_sales']
        .reduce((sum, k) => sum + Number(this.h[k] || 0), 0);
    },
    salesSources() {
      return ['sales', 'invoice_sales', 'membership_sales', 'reservation_sales']
        .filter(k => Number(this.h[k] || 0) > 0).length;
    },
    pnl() {
      return Number(this.h.profits || 0) - Number(this.h.expenses || 0) - Number(this.h.wastes || 0);
    },
    payments() {
      const p = this.h.payments || {};
      return Object.fromEntries(Object.entries(p).filter(([k]) => k));
    },
    cashPaid() {
      const key = Object.keys(this.payments).find(k => k.toLowerCase().includes('cash'));
      return key ? Number(this.payments[key] || 0) : 0;
    },
    transactions() {
      return Object.values(this.h.allTransactions || {}).filter(Boolean);
    },
    itemsSold() {
      return Object.values(this.h.items || {});
    }
  },

  watch: {
    params: { deep: true, handler() { this.load(); } }
  },

  mounted() {
    this.load();
  },

  methods: {
    // Lets the dashboard's sidebar "View all transactions" open this same list.
    openTransactions() {
      this.$refs.transactionsModal.show();
    },

    load() {
      this.loading = true;
      axios.get('/api/highlights', { params: this.params })
        .then(({ data }) => { this.h = data || {}; })
        .catch(() => { this.h = {}; })
        .finally(() => { this.loading = false; });

      if (this.isInvoiceOnly) return;
      this.analysisLoaded = false;
      axios.get('/api/inventory-analysis', { params: this.params })
        .then(({ data }) => { this.inventoryAnalysis = Object.values(data || {}); })
        .catch(() => { this.inventoryAnalysis = []; })
        .finally(() => { this.analysisLoaded = true; });
    },

    ordersUrl(extra) {
      return `/orders?${extra}start-date=${this.startDate}&end-date=${this.endDate}`;
    },

    itemLink(id) {
      return `/inventory-activity?only-sales=1&p-identifier-id=${id}&start-date=${this.startDate}&end-date=${this.endDate}`;
    },

    downloadInventoryAnalysis() {
      this.downloading = true;
      axios.get('/api/download-inventory-analysis', {
        params: { start_date: this.startDate, end_date: this.endDate },
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Inventory-analysis-' + moment().format('H-mm-DD-MM-YYYY') + '.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }).finally(() => { this.downloading = false; });
    },

    money(value) {
      return 'KES ' + Math.abs(Number(value || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
};
</script>

<style scoped>
.breakdown-card {
  border-radius: 14px;
  border-color: #e9ecef !important;
  padding: 20px;
}
.breakdown-title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(253, 71, 25, 0.8);
  margin-bottom: 8px;
}
.breakdown-hero {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin-bottom: 10px;
}
.breakdown-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
  border-top: 1px solid #f1f5f9;
  font-size: 13px;
  color: #0f172a;
}
.breakdown-row.text-danger { color: #dc3545; }
.breakdown-row.text-secondary { color: #64748b; }
.breakdown-link:hover {
  text-decoration: none;
  background: #f8fafc;
}
</style>
